import axios from 'axios'
import { useAuthStore } from '../store/authStore'

export interface SignupDetails {
  username: string
  full_name: string
  company: string
  city: string
  country: string
  address: string
  course_slug: string
}

export interface SignupCourse {
  id: string
  slug: string
  title: string
  is_free: boolean
}

export interface CourseLesson {
  id: string
  title: string
  is_preview: boolean
  has_quiz: boolean
  is_completed: boolean
  is_accessible: boolean
  lock_reason: string | null
  lesson_type: 'article' | 'lab' | 'video'
  position: number
  video_duration_seconds: number | null
}

export interface LessonDetail extends CourseLesson {
  module_id: string
  content_md: string | null
  video_url: string | null
}

export interface QuizDetail {
  id: string
  title: string
  pass_score: number
  max_attempts: number
  attempts_used: number
  has_passed: boolean
  content_revision: number
  questions: {
    id: string
    body: string
    question_type: 'mcq' | 'true_false' | 'multi_select' | 'short_answer'
    choices: { id: string; text: string }[] | null
  }[]
}

export interface QuizResult {
  attempt_id: string
  score: number
  passed: boolean
  pass_score: number
  correct_count: number
  total_questions: number
  attempts_used: number
  attempts_remaining: number
  feedback: Record<string, { correct: boolean; explanation: string }>
}

export interface CourseModule {
  id: string
  title: string
  description: string | null
  lab_url: string | null
  lessons: CourseLesson[]
}

export interface CourseDetail {
  id: string
  slug: string
  title: string
  subtitle: string | null
  description: string | null
  thumbnail_url: string | null
  level: string
  tags: string[] | null
  is_free: boolean
  is_enrollment_open: boolean
  is_enrolled: boolean
  is_enrollment_pending: boolean
  lesson_count: number
  enrolled_count: number
  book_url: string | null
  lab_url: string | null
  modules: CourseModule[]
  instructor: { id: string; username: string; full_name: string | null }
}

export interface AcademyProgram {
  slug: string
  title: string
  description: string
  status: 'coming_soon'
  courses: { position: number; slug: string; title: string; is_enrollment_open: boolean }[]
}

export interface CourseCertificate {
  id: string
  course_title: string
  learner_name: string
  issued_at: string
  verify_token: string
  verify_url: string
}

export interface CourseProgress {
  course_id: string
  total_lessons: number
  completed_lessons: number
  progress_percent: number
  completed: boolean
  certificate_available: boolean
  next_lesson_id: string | null
}

export function apiErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const detail: unknown = error.response?.data?.detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail)) {
      const messages = detail.flatMap((entry: unknown) => {
        if (entry && typeof entry === 'object' && 'msg' in entry && typeof entry.msg === 'string') {
          const field = 'loc' in entry && Array.isArray(entry.loc) ? entry.loc.slice(1).join('.') : ''
          return [field ? `${field}: ${entry.msg}` : entry.msg]
        }
        return []
      })
      if (messages.length) return messages.join('; ')
    }
  }
  return fallback
}

export const api = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' },
})

// Attach Bearer token
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

// Auto-refresh on 401
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true
      const { refreshToken, setTokens, logout } = useAuthStore.getState()
      if (refreshToken) {
        try {
          const { data } = await axios.post('/api/v1/auth/refresh', { refresh_token: refreshToken })
          setTokens(data.access_token, data.refresh_token)
          original.headers.Authorization = `Bearer ${data.access_token}`
          return api(original)
        } catch {
          logout()
        }
      } else {
        logout()
      }
    }
    return Promise.reject(error)
  },
)

// ── Auth ────────────────────────────────────────────────────────────────────

export const authApi = {
  register: (data: SignupDetails & { email: string; password: string }) =>
    api.post('/auth/register', data),
  verifyEmail: (email: string, code: string) =>
    api.post('/auth/verify-email', { email, code }),
  login: (username: string, password: string) =>
    api.post<{ access_token: string; refresh_token: string; expires_in: number; must_change_password: boolean }>('/auth/login', { username, password }),
  me: () => api.get('/auth/me'),
  updateMe: (data: Partial<{ full_name: string; bio: string; avatar_url: string }>) =>
    api.patch('/auth/me', data),
  changePassword: (current_password: string, new_password: string) =>
    api.post('/auth/change-password', { current_password, new_password }),
  setupStatus: () => api.get<{ setup_required: boolean }>('/auth/setup-status'),
  googleLoginUrl: () => api.get<{ redirect_url: string; state: string }>('/auth/google'),
  googleSignup: (data: SignupDetails) =>
    api.post<{ redirect_url: string; state: string }>('/auth/google/signup', data),
  googleCallback: (code: string, state: string) =>
    api.get<{ access_token: string; refresh_token: string; expires_in: number; is_new_user: boolean; must_change_password: boolean; course_slug: string | null; course_access_granted: boolean | null }>(
      `/auth/google/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`
    ),
}

// ── Courses ─────────────────────────────────────────────────────────────────

export const coursesApi = {
  signupOptions: async (): Promise<SignupCourse[]> => {
    const courses: SignupCourse[] = []
    let page = 1
    let pages = 1
    do {
      const { data } = await api.get<{ items: SignupCourse[]; pages: number }>('/courses', { params: { page, page_size: 100, enrollment_open: true } })
      courses.push(...data.items)
      pages = data.pages
      page += 1
    } while (page <= pages)
    return courses
  },
  list: (params?: { page?: number; tag?: string; level?: string; search?: string }) =>
    api.get('/courses', { params }),
  get: (slug: string) => api.get<CourseDetail>(`/courses/${slug}`),
  create: (data: object) => api.post('/courses', data),
  update: (slug: string, data: object) => api.patch(`/courses/${slug}`, data),
  delete: (slug: string) => api.delete(`/courses/${slug}`),
  enroll: (slug: string) => api.post(`/courses/${slug}/enroll`),
  getLesson: (slug: string, lessonId: string) => api.get<LessonDetail>(`/courses/${slug}/lessons/${lessonId}`),
  updateProgress: (slug: string, lessonId: string, completed: boolean, watchSeconds = 0) =>
    api.post<CourseProgress>(`/courses/${slug}/lessons/${lessonId}/progress?completed=${completed}&watch_seconds=${watchSeconds}`),
  getCourseProgress: (slug: string) => api.get<CourseProgress>(`/courses/${slug}/progress`),
}

export const programsApi = {
  list: () => api.get<AcademyProgram[]>('/programs'),
  get: (slug: string) => api.get<AcademyProgram>(`/programs/${slug}`),
}

// ── Quizzes ──────────────────────────────────────────────────────────────────

export const quizApi = {
  get: (slug: string, lessonId: string) => api.get<QuizDetail>(`/courses/${slug}/lessons/${lessonId}/quiz`),
  submit: (slug: string, lessonId: string, answers: Record<string, string[]>, contentRevision: number) =>
    api.post<QuizResult>(`/courses/${slug}/lessons/${lessonId}/quiz/submit`, { answers, content_revision: contentRevision }),
}

// ── Certificates ─────────────────────────────────────────────────────────────

export const certificatesApi = {
  myCertificates: () => api.get<CourseCertificate[]>('/users/me/certificates'),
  verify: (token: string) => api.get(`/certificates/${token}/verify`),
  downloadPdf: (token: string) => api.get<Blob>(`/certificates/${token}/pdf`, { responseType: 'blob' }),
}

// ── Admin ────────────────────────────────────────────────────────────────────

export const adminApi = {
  getStats: () => api.get('/admin/stats'),
  listUsers: (skip = 0, limit = 100) => api.get('/admin/users', { params: { skip, limit } }),
  updateUser: (userId: string, data: { role?: string; is_active?: boolean; email_verified?: boolean }) =>
    api.patch(`/admin/users/${userId}`, data),
  deleteUser: (userId: string) => api.delete(`/admin/users/${userId}`),
  listCourses: (skip = 0, limit = 100) => api.get('/admin/courses', { params: { skip, limit } }),
  updateCourse: (courseId: string, data: { is_published?: boolean }) =>
    api.patch(`/admin/courses/${courseId}`, data),
  deleteCourse: (courseId: string) => api.delete(`/admin/courses/${courseId}`),
}
